import { Button } from "@/components/ui/button";
import { Shield, Rocket, TrendingUp } from "lucide-react";
import ContactFormDialog from "./ContactFormDialog";
const Hero = () => {
  return <section className="relative min-h-screen flex items-center justify-center gradient-hero overflow-hidden bg-dark-section-bg">
      {/* Base grid pattern - fine mesh */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,hsl(var(--dark-border)/0.15)_1px,transparent_1px),linear-gradient(to_bottom,hsl(var(--dark-border)/0.15)_1px,transparent_1px)] bg-[size:2rem_2rem]" />
      
      {/* Larger blueprint grid overlay */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,hsl(var(--primary)/0.1)_1px,transparent_1px),linear-gradient(to_bottom,hsl(var(--primary)/0.1)_1px,transparent_1px)] bg-[size:8rem_8rem]" />
      
      {/* Diagonal tech lines */}
      <div className="absolute inset-0 opacity-20">
        <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="diagonalLines" patternUnits="userSpaceOnUse" width="100" height="100" patternTransform="rotate(45)">
              <line x1="0" y1="0" x2="0" y2="100" stroke="hsl(var(--primary))" strokeWidth="0.5" strokeDasharray="4 8" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#diagonalLines)" />
        </svg>
      </div>

      {/* 3D Mesh / Topographic contour lines - left side */}
      <div className="absolute left-0 top-1/4 w-1/2 h-1/2 opacity-30 pointer-events-none">
        <svg viewBox="0 0 400 400" className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <linearGradient id="meshGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity="0.6" />
              <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity="0" />
            </linearGradient>
          </defs>
          {/* Contour ellipses */}
          <ellipse cx="200" cy="200" rx="180" ry="60" fill="none" stroke="url(#meshGradient)" strokeWidth="0.5" transform="rotate(-15 200 200)" />
          <ellipse cx="200" cy="200" rx="150" ry="50" fill="none" stroke="url(#meshGradient)" strokeWidth="0.5" transform="rotate(-15 200 200)" />
          <ellipse cx="200" cy="200" rx="120" ry="40" fill="none" stroke="url(#meshGradient)" strokeWidth="0.5" transform="rotate(-15 200 200)" />
          <ellipse cx="200" cy="200" rx="90" ry="30" fill="none" stroke="url(#meshGradient)" strokeWidth="0.5" transform="rotate(-15 200 200)" />
          <ellipse cx="200" cy="200" rx="60" ry="20" fill="none" stroke="url(#meshGradient)" strokeWidth="0.5" transform="rotate(-15 200 200)" />
          <ellipse cx="200" cy="200" rx="30" ry="10" fill="none" stroke="url(#meshGradient)" strokeWidth="0.5" transform="rotate(-15 200 200)" />
        </svg>
      </div>

      {/* Data grid / circuit pattern - right side */}
      <div className="absolute right-0 bottom-1/4 w-96 h-96 opacity-20 pointer-events-none">
        <svg viewBox="0 0 200 200" className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <linearGradient id="circuitGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity="0" />
              <stop offset="50%" stopColor="hsl(var(--primary))" stopOpacity="0.8" />
              <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity="0" />
            </linearGradient>
          </defs>
          {/* Horizontal data lines */}
          <line x1="0" y1="40" x2="200" y2="40" stroke="url(#circuitGradient)" strokeWidth="0.5" />
          <line x1="0" y1="80" x2="200" y2="80" stroke="url(#circuitGradient)" strokeWidth="0.5" />
          <line x1="0" y1="120" x2="200" y2="120" stroke="url(#circuitGradient)" strokeWidth="0.5" />
          <line x1="0" y1="160" x2="200" y2="160" stroke="url(#circuitGradient)" strokeWidth="0.5" />
          {/* Vertical connectors */}
          <line x1="40" y1="0" x2="40" y2="200" stroke="url(#circuitGradient)" strokeWidth="0.5" />
          <line x1="100" y1="0" x2="100" y2="200" stroke="url(#circuitGradient)" strokeWidth="0.5" />
          <line x1="160" y1="0" x2="160" y2="200" stroke="url(#circuitGradient)" strokeWidth="0.5" />
          {/* Node points */}
          <circle cx="40" cy="40" r="3" fill="hsl(var(--primary))" opacity="0.6" />
          <circle cx="100" cy="80" r="3" fill="hsl(var(--primary))" opacity="0.6" />
          <circle cx="160" cy="120" r="3" fill="hsl(var(--primary))" opacity="0.6" />
          <circle cx="40" cy="160" r="3" fill="hsl(var(--primary))" opacity="0.6" />
          <circle cx="100" cy="40" r="2" fill="hsl(var(--primary))" opacity="0.4" />
          <circle cx="160" cy="80" r="2" fill="hsl(var(--primary))" opacity="0.4" />
        </svg>
      </div>

      {/* Hexagonal mesh pattern - top right */}
      <div className="absolute -top-20 -right-20 w-[500px] h-[500px] opacity-15 pointer-events-none">
        <svg viewBox="0 0 300 300" className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="hexPattern" patternUnits="userSpaceOnUse" width="40" height="69.28" patternTransform="scale(1.5)">
              <polygon points="20,0 40,11.55 40,34.64 20,46.19 0,34.64 0,11.55" fill="none" stroke="hsl(var(--primary))" strokeWidth="0.5" />
              <polygon points="20,46.19 40,57.74 40,80.83 20,92.38 0,80.83 0,57.74" fill="none" stroke="hsl(var(--primary))" strokeWidth="0.5" transform="translate(20, 23.09)" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#hexPattern)" />
        </svg>
      </div>

      {/* Animated scanning line */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute w-full h-px bg-gradient-to-r from-transparent via-primary/40 to-transparent animate-scan" />
      </div>
      
      {/* Gradient orbs */}
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-3xl" />
      <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-primary/10 rounded-full blur-3xl" />
      
      {/* Corner accent lines */}
      <div className="absolute top-8 left-8 w-32 h-32 border-l border-t border-primary/20" />
      <div className="absolute bottom-8 right-8 w-32 h-32 border-r border-b border-primary/20" />

      <div className="container relative z-10 px-6 py-24 md:py-32">
        <div className="max-w-4xl mx-auto text-center">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-dark-section-border bg-dark-section-card/50 backdrop-blur-sm mb-8 animate-fade-up">
            <Shield className="w-4 h-4 text-primary" />
            <span className="text-sm text-dark-section-muted">Deep-Tech & Dual-Use Specialists</span>
          </div>

          {/* Main heading */}
          <h1 className="font-display text-[34px] md:text-[46px] lg:text-[58px] font-bold text-dark-section-foreground mb-6 leading-tight">
            Fundraising & Strategy for{' '}
            <span className="text-gradient-gold whitespace-nowrap">
              Deep-Tech and Dual-Use
            </span>{' '}
            <span className="text-dark-section-foreground text-[32px] md:text-[44px] lg:text-[56px]">Companies</span>
          </h1>

          {/* Subheading */}
          <p className="text-lg md:text-xl text-dark-section-foreground/80 mb-10 max-w-3xl mx-auto animate-fade-up" style={{
          animationDelay: '0.2s'
        }}>
            Investment readiness, pitch materials, investor sourcing, and full fundraising execution.
          </p>

          {/* CTA button */}
          <div className="flex items-center justify-center mb-16 animate-fade-up" style={{
          animationDelay: '0.4s'
        }}>
            <ContactFormDialog />
          </div>

          {/* Trust statement */}
          <p className="text-sm text-dark-section-muted animate-fade-up" style={{
          animationDelay: '0.5s'
        }}>
            Trusted by founders and operators across Europe and emerging tech markets.
          </p>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mt-16 pt-16 border-t border-dark-section-border animate-fade-up" style={{
          animationDelay: '0.6s'
        }}>
            <div className="text-center">
              <div className="flex items-center justify-center mb-2">
                <Rocket className="w-5 h-5 text-primary mr-2" />
                <span className="text-2xl md:text-3xl font-display font-bold text-dark-section-foreground">120+</span>
              </div>
              <p className="text-sm text-dark-section-muted">EU/UK Investors</p>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center mb-2">
                <TrendingUp className="w-5 h-5 text-primary mr-2" />
                <span className="text-2xl md:text-3xl font-display font-bold text-dark-section-foreground">€20M+</span>
              </div>
              <p className="text-sm text-dark-section-muted">Reviewed or supported</p>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center mb-2">
                <TrendingUp className="w-5 h-5 text-primary mr-2" />
                <span className="text-2xl md:text-3xl font-display font-bold text-dark-section-foreground">10+</span>
              </div>
              <p className="text-sm text-dark-section-muted">Years in Tech & Strategy</p>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center mb-2">
                <Shield className="w-5 h-5 text-primary mr-2" />
                <span className="text-2xl md:text-3xl font-display font-bold text-dark-section-foreground">100%</span>
              </div>
              <p className="text-sm text-dark-section-muted">Confidential</p>
            </div>
          </div>
        </div>
      </div>
    </section>;
};
export default Hero;